import EPIssueButton from 'editor-components/header/EPIssueButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import EditorApprovalButtons from '../approvals/EditorApprovalButtons';
import { useCallback, useMemo } from 'react';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { PublishTypes } from '../../constants/PublishTypes';
import { PublishActionModalNames } from '../publishing/types/publishButtonTypes';
import useEditorPublishProps from '../publishing/hooks/useEditorPublishProps';
import EditorPublishButtonActionModals from '../publishing/modals/EditorPublishButtonActionModals';
import useContentCrmObject from '../publishing/hooks/useContentCrmObject';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue } from 'content-management-data-lib';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EditorPublishButton = ({
  cannotPublishOnDomainReason,
  errors,
  warnings,
  infos,
  canSchedule,
  publishEnabled,
  unpublishEnabled,
  testVariationTooltipPropsForApproval = {
    isTestVariation: false,
    testVariationDisabledTooltip: undefined
  },
  extraDropdownActions,
  buttonTextKey,
  updateFocusedItemAction,
  tooltipOverride,
  lockedPublishButtonProps,
  customPublishActionProps = {},
  publish,
  unpublish,
  schedulePublishUnpublish,
  isNotAuthorizedToPublish = false,
  confirmPublishActionProps,
  schedulingOptionsModalProps = {},
  buttonSize,
  unpublishFooter,
  publishFooter,
  getSchedulePublishDescription,
  confirmScheduleButtonTextKey
}) => {
  const {
    publishDropdownActions,
    approvalDropdownActions,
    isPublishEnabled,
    isPublishInProgress,
    loadingFailed,
    publishErrors,
    publishWarnings,
    publishInfos,
    errorsAndWarningsAndInfosCount,
    onIssueClick,
    handleMouseEnterButton,
    handleTrackOnDropdownCompleteAction,
    openModal,
    closeModal,
    openModalName,
    cancelScheduleModalType,
    setCancelScheduleModalType
  } = useEditorPublishProps({
    canSchedule,
    cannotPublishOnDomainReason,
    lockedPublishButtonProps,
    extraDropdownActions,
    unpublishEnabled,
    publishEnabled,
    errors,
    warnings,
    infos,
    updateFocusedItemAction
  });
  const {
    contentCrmObjectPublishType,
    isContentCrmObjectLoading,
    contentCrmObject
  } = useContentCrmObject();
  const finalButtonTextKey = useMemo(() => {
    if (isNotAuthorizedToPublish) {
      return `header.publishButton.notAuthorized.label`;
    }
    return buttonTextKey || `header.publishButton.${contentCrmObjectPublishType}.label`;
  }, [buttonTextKey, contentCrmObjectPublishType, isNotAuthorizedToPublish]);
  const handlePublishButtonClick = useCallback(() => {
    if (lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.isLocked && lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.onClickLocked) {
      lockedPublishButtonProps.onClickLocked();
      return;
    }
    if (customPublishActionProps !== null && customPublishActionProps !== void 0 && customPublishActionProps.shouldUseCustomPublishHandler && customPublishActionProps !== null && customPublishActionProps !== void 0 && customPublishActionProps.customPublishHandler) {
      customPublishActionProps.customPublishHandler();
      return;
    }
    if (confirmPublishActionProps !== null && confirmPublishActionProps !== void 0 && confirmPublishActionProps.shouldConfirmPublishAction) {
      openModal(PublishActionModalNames.PUBLISH_CONFIRM);
      return;
    }
    if (contentCrmObjectPublishType === PublishTypes.schedule && schedulePublishUnpublish && contentCrmObject) {
      schedulePublishUnpublish({
        schedulePublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE)
      });
      return;
    }
    publish(contentCrmObjectPublishType);
  }, [confirmPublishActionProps === null || confirmPublishActionProps === void 0 ? void 0 : confirmPublishActionProps.shouldConfirmPublishAction, contentCrmObject, contentCrmObjectPublishType, customPublishActionProps, lockedPublishButtonProps, openModal, publish, schedulePublishUnpublish]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EditorApprovalButtons, Object.assign({
      errorsAndWarningsAndInfosCount: errorsAndWarningsAndInfosCount,
      onOpenWarningsAndSuggestionsPanel: () => openModal(PublishActionModalNames.WARNINGS_AND_SUGGESTIONS),
      buttonSize: "sm",
      extraActionButtons: approvalDropdownActions
    }, testVariationTooltipPropsForApproval, {
      children: /*#__PURE__*/_jsx("div", {
        "data-onboarding": "onboarding-publish-button",
        "data-campaign-assistant-onboarding-tour": "tour-step-publish",
        children: /*#__PURE__*/_jsxs(EPIssueButton, {
          actions: publishDropdownActions,
          "data-test": "publish-issue-button",
          errors: publishErrors,
          warnings: publishWarnings,
          infos: publishInfos,
          tooltipOverride: tooltipOverride,
          onButtonClick: handlePublishButtonClick,
          onDropdownOpenComplete: handleTrackOnDropdownCompleteAction,
          onIssueClick: onIssueClick,
          onMouseEnter: handleMouseEnterButton,
          disabled: !isPublishEnabled || isContentCrmObjectLoading,
          loading: isPublishInProgress,
          loadingFailed: loadingFailed,
          buttonSize: buttonSize,
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: finalButtonTextKey
          }), lockedPublishButtonProps !== null && lockedPublishButtonProps !== void 0 && lockedPublishButtonProps.isLocked ? /*#__PURE__*/_jsx(IconHolder, {
            padding: "0",
            icon: LockedIcon
          }) : null]
        })
      })
    })), /*#__PURE__*/_jsx(EditorPublishButtonActionModals, {
      publishErrors: publishErrors,
      publishWarnings: publishWarnings,
      publishInfos: publishInfos,
      buttonTextKey: finalButtonTextKey,
      onIssueClick: onIssueClick,
      publish: publish,
      unpublish: unpublish,
      openModal: openModal,
      closeModal: closeModal,
      schedulePublishUnpublish: schedulePublishUnpublish,
      confirmPublishActionProps: confirmPublishActionProps,
      schedulingOptionsModalProps: schedulingOptionsModalProps,
      openModalName: openModalName,
      cancelScheduleModalType: cancelScheduleModalType,
      setCancelScheduleModalType: setCancelScheduleModalType,
      unpublishFooter: unpublishFooter,
      publishFooter: publishFooter,
      getSchedulePublishDescription: getSchedulePublishDescription,
      confirmScheduleButtonTextKey: confirmScheduleButtonTextKey
    })]
  });
};
export default EditorPublishButton;